// Ficha del ticket (artboard f): cabecera con estado y semáforo, acciones
// según el papel, stepper de estados con quién y cuándo, qué se pidió,
// asignación con sugerencia, conversación e historial, detalles, quién mueve
// cada estado y tiempo en cada estado.
import * as React from "react";
import { Link, useParams } from "@tanstack/react-router";
import {
  Boton,
  Campo,
  Dialog,
  DialogContenido,
  DialogDescripcion,
  DialogEncabezado,
  DialogPie,
  DialogTitulo,
  EsqueletoTabla,
  Pill,
  Select,
  SelectContenido,
  SelectDisparador,
  SelectOpcion,
  SelectValor,
  Textarea,
  TituloPagina,
  Vacio,
  cn,
  useToast,
} from "@iavmx/ui";
import { useAccionTicket, useCatalogo, useCronLocal, useDetalle, usePersona, type Detalle } from "./datos";
import { ErrorCarga, FilaDato, Migas, PillEstado, PorConfirmar, Seccion, SemaforoTicket } from "./comunes";
import { ETIQUETA_CANAL, ETIQUETA_PRIORIDAD, FLUJO, estadoLabel, fechaCorta, fechaHora, fechaLarga, folio, horas, slaTexto } from "./formato";

type Prioridad = "baja" | "normal" | "alta" | "urgente";

function Stepper({ d }: { d: Detalle }) {
  const cambios = d.historial.filter((h) => h.campo === "estado");
  const actual = d.ticket.estado;
  const idxActual = FLUJO.indexOf(actual as (typeof FLUJO)[number]);
  const cuando = (e: string) => {
    const ultimo = [...cambios].reverse().find((h) => h.nuevo === e);
    if (e === "nueva") return `${d.ticket.solicitante?.nombre ?? "—"} · ${fechaCorta(d.ticket.creadoEn)}`;
    if (!ultimo) return d.quienMueve.find((q) => q.estado === e)?.quien ?? "";
    return `${ultimo.por?.nombre ?? "Sistema"} · ${fechaHora(ultimo.creadoEn)}`;
  };
  if (actual === "cancelada")
    return (
      <p className="border-t border-linea pt-4 font-ui text-sm text-tinta-tenue">
        Cancelado el {fechaHora(d.ticket.cerradaEn)}.
      </p>
    );
  return (
    <ol className="grid grid-cols-3 gap-3 border-t border-linea pt-4 md:grid-cols-6" aria-label="Estados del ticket">
      {FLUJO.map((e, i) => {
        const hecho = i < idxActual;
        const esActual = i === idxActual;
        return (
          <li key={e} className="flex flex-col gap-1.5" aria-current={esActual ? "step" : undefined}>
            <div className={cn("h-1 w-full", hecho ? "bg-tinta" : esActual ? "bg-azul" : "bg-fondo-alzado")} />
            <span className={cn("font-ui text-sm font-semibold", hecho || esActual ? "text-tinta" : "text-tinta-tenue")}>{estadoLabel(e)}</span>
            <span className="font-ui text-xs text-tinta-tenue">{cuando(e)}</span>
          </li>
        );
      })}
    </ol>
  );
}

function DialogoComentario({
  abierto,
  onCerrar,
  titulo,
  descripcion,
  accion,
  onConfirmar,
  pendiente,
  requerido,
}: {
  abierto: boolean;
  onCerrar: () => void;
  titulo: string;
  descripcion: string;
  accion: string;
  onConfirmar: (texto: string) => void;
  pendiente: boolean;
  requerido?: boolean;
}) {
  const [texto, setTexto] = React.useState("");
  // El texto se limpia al cerrar, no en un efecto: así no hay render en cascada.
  const cerrar = () => {
    setTexto("");
    onCerrar();
  };
  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && cerrar()}>
      <DialogContenido>
        <DialogEncabezado>
          <DialogTitulo>{titulo}</DialogTitulo>
          <DialogDescripcion>{descripcion}</DialogDescripcion>
        </DialogEncabezado>
        <Campo etiqueta="Comentario" htmlFor="comentario-dialogo">
          <Textarea id="comentario-dialogo" value={texto} onChange={(e) => setTexto(e.target.value)} className="text-base md:text-sm" autoFocus />
        </Campo>
        <DialogPie>
          <Boton variante="contorno" type="button" onClick={cerrar}>
            Volver
          </Boton>
          <Boton variante="primario" type="button" disabled={pendiente || (requerido && texto.trim().length === 0)} onClick={() => onConfirmar(texto.trim())}>
            {pendiente ? "Guardando…" : accion}
          </Boton>
        </DialogPie>
      </DialogContenido>
    </Dialog>
  );
}

export function DetalleTicket() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const q = useDetalle(id ?? "");
  const cat = useCatalogo();
  const yo = usePersona();
  const acciones = useAccionTicket(id ?? "");
  const cron = useCronLocal();
  const { avisar } = useToast();
  const [dialogo, setDialogo] = React.useState<"cambios" | "cancelar" | null>(null);
  const [personaElegida, setPersonaId] = React.useState("");
  const [nota, setNota] = React.useState("");
  const [comentario, setComentario] = React.useState("");

  const d = q.data;
  // La sugerencia de IA es el valor por defecto del selector hasta que la jefatura elija otra persona.
  const personaId = personaElegida || d?.sugerencia?.sugerido?.id || "";

  if (!id) return <Vacio titulo="Falta el ticket" />;
  if (q.isLoading) return <EsqueletoTabla filas={8} columnas={3} />;
  if (q.error || !d) return <ErrorCarga error={q.error ?? new Error("Sin datos")} titulo="No se pudo abrir el ticket" />;
  const t = d.ticket;
  const ahora = new Date(d.ahora);
  const error = (e: Error) => avisar({ titulo: "No se pudo", descripcion: e.message, tono: "error" });
  const mover = (estado: string, comentarioTexto?: string) =>
    acciones.mover.mutate(
      { estado, comentario: comentarioTexto || undefined },
      {
        onSuccess: () => {
          setDialogo(null);
          avisar({ titulo: `Ticket ${estadoLabel(estado).toLowerCase()}`, tono: "exito" });
        },
        onError: error,
      },
    );
  const transiciones = d.acciones.transiciones;
  const principal = transiciones.find((x) => !["cancelada", "en_proceso"].includes(x.a) || (x.a === "en_proceso" && t.estado === "asignada"));
  const pedirCambios = transiciones.find((x) => x.a === "en_proceso" && t.estado === "en_revision");
  const cancelar = transiciones.find((x) => x.a === "cancelada");
  const candidatos = d.sugerencia?.candidatos ?? [];
  const personasArea = cat.data?.personas.filter((p) => (d.sugerencia?.area ? p.areaId === d.sugerencia.area.id : true)) ?? [];
  const opcionesAsignar = personasArea.length > 0 ? personasArea.map((p) => ({ id: p.id, nombre: p.nombre, abiertos: candidatos.find((c) => c.id === p.id)?.abiertos ?? 0 })) : (cat.data?.personas ?? []).map((p) => ({ id: p.id, nombre: p.nombre, abiertos: 0 }));
  const eventos = [
    ...d.comentarios.map((c) => ({ id: c.id, en: c.creadoEn, quien: c.autor?.nombre ?? "—", que: c.texto, tipo: "comentario" as const })),
    ...d.historial
      .filter((h) => h.campo !== "escalada_a" || h.nuevo)
      .map((h) => ({
        id: h.id,
        en: h.creadoEn,
        quien: h.por?.nombre ?? "Sistema",
        tipo: "cambio" as const,
        que:
          h.campo === "estado"
            ? h.anterior ? `Movió el ticket de ${estadoLabel(String(h.anterior))} a ${estadoLabel(String(h.nuevo))}` : "Creó el ticket"
            : h.campo === "asignada_a"
              ? `Asignó a ${cat.data?.personas.find((p) => p.id === h.nuevo)?.nombre ?? "alguien"}`
              : h.campo === "prioridad"
                ? `Cambió la prioridad de ${ETIQUETA_PRIORIDAD[String(h.anterior)]?.toLowerCase()} a ${ETIQUETA_PRIORIDAD[String(h.nuevo)]?.toLowerCase()}`
                : h.campo === "vence_en"
                  ? `Movió la fecha límite a ${fechaHora(String(h.nuevo))}`
                  : h.campo === "escalada_a"
                    ? `Venció y se escaló a ${cat.data?.personas.find((p) => p.id === h.nuevo)?.nombre ?? "la jefatura"}`
                    : `Cambió ${h.campo}`,
      })),
  ].sort((a, b) => b.en.localeCompare(a.en));

  return (
    <>
      <Migas partes={[{ texto: "Tickets", to: "/tickets" }, { texto: folio(t.folio) }]} />
      <TituloPagina
        accion={
          transiciones.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {cancelar && (
                <Boton variante="fantasma" type="button" onClick={() => setDialogo("cancelar")}>
                  Cancelar ticket
                </Boton>
              )}
              {pedirCambios && (
                <Boton variante="contorno" type="button" onClick={() => setDialogo("cambios")}>
                  Pedir cambios
                </Boton>
              )}
              {principal && principal.a !== "asignada" && (
                <Boton variante="acento" type="button" disabled={acciones.mover.isPending} onClick={() => mover(principal.a)}>
                  {acciones.mover.isPending ? "Guardando…" : principal.accion}
                </Boton>
              )}
              {principal?.a === "asignada" && (
                <Boton variante="acento" type="button" asChild>
                  <a href="#asignacion">Asignar</a>
                </Boton>
              )}
            </div>
          ) : (
            <p className="font-ui text-sm text-tinta-tenue">
              {t.estado === "cerrada" || t.estado === "cancelada" ? "Este ticket ya no se mueve." : `Ahora mueve este ticket ${d.quienMueve.find((x) => x.estado === FLUJO[FLUJO.indexOf(t.estado as (typeof FLUJO)[number]) + 1])?.quien.toLowerCase() ?? "otra persona"}.`}
            </p>
          )
        }
      >
        {t.titulo}
      </TituloPagina>
      <div className="-mt-3 mb-6 flex flex-wrap items-center gap-3 font-ui text-sm text-tinta-tenue">
        <PillEstado estado={t.estado} />
        <SemaforoTicket ticket={t} ahora={ahora} />
        <span>
          {t.categoria?.nombre ?? "Sin categoría"} · SLA {slaTexto(t.slaHoras)} · prioridad {ETIQUETA_PRIORIDAD[t.prioridad]?.toLowerCase()}
        </span>
        {t.escaladaA && <Pill tono="rojo">Escalado a {t.escaladaA.nombre}</Pill>}
      </div>

      <Stepper d={d} />

      <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="flex flex-col gap-8">
          <Seccion titulo="Qué se pidió">
            {t.descripcion && <p className="max-w-prose font-ui text-sm text-tinta-2">{t.descripcion}</p>}
            {t.textoOriginal && (
              <blockquote className="max-w-prose whitespace-pre-line border-l-2 border-linea-fuerte pl-4 font-ui text-sm text-tinta-2">{t.textoOriginal}</blockquote>
            )}
            {!t.descripcion && !t.textoOriginal && <p className="font-ui text-sm text-tinta-tenue">Sin descripción: solo el título.</p>}
            <p className="font-ui text-xs text-tinta-tenue">
              {t.origenIa ? `Creado desde un mensaje de ${ETIQUETA_CANAL[t.canalOrigen] ?? t.canalOrigen}; la propuesta de categoría, área y fecha la confirmó ${t.solicitante?.nombre ?? "una persona"}.` : `Capturado por ${ETIQUETA_CANAL[t.canalOrigen]?.toLowerCase() ?? t.canalOrigen} por ${t.solicitante?.nombre ?? "—"}.`}
              {t.fechaCompromiso && ` Lo necesitan el ${fechaLarga(t.fechaCompromiso)}.`}
            </p>
          </Seccion>

          {d.acciones.puedeAsignar && (
            <Seccion titulo={t.asignada ? "Reasignar" : "Asignar"} className="scroll-mt-20" extra={d.sugerencia?.area ? `Personas de ${d.sugerencia.area.nombre}` : "Sin área destino: elige a cualquiera"}>
              {/* Con responsable ya asignado, el panel va plegado: reasignar es la excepción, no el siguiente paso. */}
              <details id="asignacion" open={!t.asignada} className="group bg-fondo-suave">
                <summary className="cursor-pointer list-none p-5 font-ui text-sm text-tinta-2 [&::-webkit-details-marker]:hidden">
                  {t.asignada ? (
                    <>
                      Responsable: <strong>{t.asignada.nombre}</strong>.{" "}
                      <span className="text-azul underline-offset-2 hover:underline group-open:hidden">Cambiar de persona</span>
                      <span className="hidden text-tinta-tenue group-open:inline">Elige a otra persona o vuelve a plegar este panel.</span>
                    </>
                  ) : (
                    <>Sin responsable todavía: elige a alguien del área.</>
                  )}
                </summary>
                <div className="flex flex-col gap-4 px-5 pb-5">
                {d.sugerencia?.sugerido ? (
                  <p className="font-ui text-sm text-tinta-2">
                    <Pill tono="azul">Sugerencia</Pill> {t.asignada ? `Si lo reasignas, la sugerencia es ${d.sugerencia.motivo}` : d.sugerencia.motivo} La decisión es de la jefatura.
                  </p>
                ) : (
                  <p className="font-ui text-sm text-tinta-tenue">Sin sugerencia: nadie del área tiene historial todavía.</p>
                )}
                <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                  <Campo etiqueta="Asignar a" htmlFor="asignar-a">
                    <Select value={personaId} onValueChange={setPersonaId}>
                      <SelectDisparador id="asignar-a" className="text-base md:text-sm">
                        <SelectValor placeholder="Elige a alguien" />
                      </SelectDisparador>
                      <SelectContenido>
                        {opcionesAsignar.map((p) => (
                          <SelectOpcion key={p.id} value={p.id}>
                            {p.nombre} · {p.abiertos} {p.abiertos === 1 ? "abierto" : "abiertos"}
                          </SelectOpcion>
                        ))}
                      </SelectContenido>
                    </Select>
                  </Campo>
                  <Campo etiqueta="Nota para la persona" htmlFor="nota" ayuda="Opcional. Se guarda como comentario.">
                    <Textarea id="nota" value={nota} onChange={(e) => setNota(e.target.value)} className="min-h-10 text-base md:text-sm" />
                  </Campo>
                </div>
                <div>
                  <Boton
                    variante={principal?.a === "asignada" ? "acento" : "primario"}
                    type="button"
                    disabled={!personaId || acciones.asignar.isPending}
                    onClick={() =>
                      acciones.asignar.mutate(
                        { personaId, nota: nota.trim() || undefined },
                        {
                          onSuccess: () => {
                            setNota("");
                            avisar({ titulo: "Ticket asignado", descripcion: "La persona recibió aviso en su bandeja y por correo si hay Resend.", tono: "exito" });
                          },
                          onError: error,
                        },
                      )
                    }
                  >
                    {acciones.asignar.isPending ? "Asignando…" : t.asignada ? "Reasignar" : "Asignar"}
                  </Boton>
                </div>
                </div>
              </details>
            </Seccion>
          )}

          <Seccion titulo="Conversación e historial">
            <form
              className="flex flex-col gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (!comentario.trim()) return;
                acciones.comentar.mutate({ texto: comentario.trim() }, { onSuccess: () => setComentario(""), onError: error });
              }}
            >
              <Campo etiqueta="Comentario" htmlFor="comentario" ayuda="Se avisa a quien pidió y a quien atiende el ticket.">
                <Textarea id="comentario" value={comentario} onChange={(e) => setComentario(e.target.value)} className="min-h-16 text-base md:text-sm" placeholder="Escribe un comentario" />
              </Campo>
              <div>
                <Boton variante="primario" type="submit" tamano="sm" disabled={!comentario.trim() || acciones.comentar.isPending}>
                  {acciones.comentar.isPending ? "Enviando…" : "Comentar"}
                </Boton>
              </div>
            </form>
            <ol className="border-t border-tinta">
              {eventos.map((e) => (
                <li key={e.id} className="grid gap-1 border-b border-linea py-3 font-ui text-sm md:grid-cols-[130px_minmax(0,1fr)] md:gap-4">
                  <span className="text-xs tabular-nums text-tinta-tenue md:text-sm">{fechaHora(e.en)}</span>
                  <div>
                    <p className={e.tipo === "comentario" ? "whitespace-pre-line text-tinta" : "text-tinta-2"}>
                      <strong>{e.quien}</strong> · {e.que}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </Seccion>
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2 bg-fondo-suave p-5">
            <span className="font-ui text-[11px] font-semibold uppercase tracking-wide text-tinta-tenue">Detalles</span>
            <FilaDato etiqueta="Solicita">
              {t.solicitante?.nombre ?? "—"}
              {t.areaSolicitante && <span className="text-tinta-tenue"> · {t.areaSolicitante.nombre}</span>}
            </FilaDato>
            <FilaDato etiqueta="Responsable">
              {t.asignada?.nombre ?? <span className="text-tinta-tenue">Sin asignar</span>}
              {t.areaAsignada && <span className="text-tinta-tenue"> · {t.areaAsignada.nombre}</span>}
            </FilaDato>
            <FilaDato etiqueta="Escala a">
              {t.escaladaA?.nombre ?? d.jefeArea?.nombre ?? "Estrategia Digital"} <PorConfirmar que="A quién se escala al vencer" />
            </FilaDato>
            <FilaDato etiqueta="Categoría">{t.categoria?.nombre ?? "—"}</FilaDato>
            <FilaDato etiqueta="Prioridad">
              {d.acciones.puedeCambiarPrioridad ? (
                <Select value={t.prioridad} onValueChange={(v) => acciones.prioridad.mutate({ prioridad: v as Prioridad }, { onError: error, onSuccess: () => avisar({ titulo: "Prioridad y fecha límite actualizadas", tono: "exito" }) })}>
                  <SelectDisparador className="h-8 w-auto gap-1 text-sm" aria-label="Cambiar prioridad">
                    <SelectValor />
                  </SelectDisparador>
                  <SelectContenido>
                    {(["baja", "normal", "alta", "urgente"] as const).map((p) => (
                      <SelectOpcion key={p} value={p}>
                        {ETIQUETA_PRIORIDAD[p]}
                      </SelectOpcion>
                    ))}
                  </SelectContenido>
                </Select>
              ) : (
                <>
                  {ETIQUETA_PRIORIDAD[t.prioridad]} <span className="text-tinta-tenue">· la cambia Estrategia Digital</span>
                </>
              )}
            </FilaDato>
            <FilaDato etiqueta="Abierto">{fechaHora(t.creadoEn)}</FilaDato>
            <FilaDato etiqueta="Fecha límite">
              <span className={cn("tabular-nums", t.vencido && "font-semibold text-rojo", t.semaforo === "ambar" && "font-semibold text-ambar")}>{fechaHora(t.venceEn)}</span>
            </FilaDato>
            {t.fechaCompromiso && <FilaDato etiqueta="Lo necesitan">{fechaLarga(t.fechaCompromiso)}</FilaDato>}
            <FilaDato etiqueta="Origen">
              {ETIQUETA_CANAL[t.canalOrigen] ?? t.canalOrigen}
              {t.origenIa && " · convertido desde el mensaje"}
            </FilaDato>
            {t.cerradaEn && <FilaDato etiqueta="Cerrado">{fechaHora(t.cerradaEn)}</FilaDato>}
          </div>

          <Seccion titulo="Quién mueve cada estado" extra={<PorConfirmar que="Quién mueve cada estado" />}>
            <div className="border-t border-tinta">
              {d.quienMueve.map((qm) => (
                <FilaDato key={qm.estado} etiqueta={estadoLabel(qm.estado)}>
                  <span className="text-tinta-2">{qm.quien}</span>
                </FilaDato>
              ))}
            </div>
          </Seccion>

          <Seccion titulo="Tiempo en cada estado">
            <div className="border-t border-tinta">
              {d.tiempos.map((x, i) => (
                <FilaDato key={i} etiqueta={estadoLabel(x.estado)}>
                  <span className="tabular-nums text-tinta-2">{x.hasta ? horas(x.horas) : `desde hace ${horas(x.horas)}`}</span>
                </FilaDato>
              ))}
            </div>
          </Seccion>

          {yo.data?.modoLocal && (
            <details className="border border-dashed border-linea-fuerte p-4 font-ui text-sm">
              <summary className="-my-2 flex min-h-11 cursor-pointer items-center font-semibold text-tinta-tenue">Herramientas de desarrollo</summary>
              <div className="mt-3 flex flex-wrap gap-2">
                <Boton variante="contorno" tamano="sm" type="button" onClick={() => acciones.simularVencimiento.mutate(undefined, { onError: error, onSuccess: () => avisar({ titulo: "Fecha límite movida a hace una hora" }) })}>
                  Simular vencimiento
                </Boton>
                <Boton variante="contorno" tamano="sm" type="button" onClick={() => cron.mutate(undefined, { onError: error, onSuccess: (r) => avisar({ titulo: "Cron corrido", descripcion: `${r.recalculados} recalculados, ${r.escalados} escalados, ${r.cerrados} cerrados.` }) })}>
                  Correr cron diario
                </Boton>
              </div>
            </details>
          )}
          <p className="font-ui text-xs text-tinta-tenue">
            <Link to="/tickets" className="inline-flex min-h-11 items-center text-azul hover:underline">
              Volver a tickets
            </Link>
          </p>
        </div>
      </div>

      <DialogoComentario
        abierto={dialogo === "cambios"}
        onCerrar={() => setDialogo(null)}
        titulo="Pedir cambios"
        descripcion="El ticket regresa a En proceso y la persona asignada recibe tu comentario."
        accion="Pedir cambios"
        requerido
        pendiente={acciones.mover.isPending}
        onConfirmar={(texto) => mover("en_proceso", texto)}
      />
      <DialogoComentario
        abierto={dialogo === "cancelar"}
        onCerrar={() => setDialogo(null)}
        titulo="Cancelar este ticket"
        descripcion="No se borra: queda como Cancelado en el historial. Di por qué."
        accion="Sí, cancelar"
        pendiente={acciones.mover.isPending}
        onConfirmar={(texto) => mover("cancelada", texto)}
      />
    </>
  );
}
